import { RawEventThreatInfo } from '@nirvana/shared/dtos/types/raw-events/gz-console';
import { ContentRendererHelper } from '../../alerts/detailsPanel/ContentRendererHelper';
import { ColumnKeys } from '@nirvana/shared/dtos/types/alerts/common';
import { Category, ThreatType } from '@nirvana/shared/dtos/types/shared/threats';
import { ActionPermissionState, ActionsPermissions, DetailsPanelEvents, SpecificsThreatData } from '../types';
import { ButtonColors, ButtonTypes } from '../../ui/gzButton/types';
import { SectionAction, SectionActionType } from '../../ui/gzGenericDetailsSection/types';
import { getFormatedDate } from '../../grid/utils/renderers';
import { ButtonHelper } from './ButtonHelper';

export class ThreatSpecificsHelper {
  private renderer: ContentRendererHelper;
  private dependencies: any;
  private buttonHelper: ButtonHelper;
  private copyConfirmationMessage: string;

  constructor(renderer: ContentRendererHelper, dependencies) {
    this.renderer = renderer;
    this.dependencies = dependencies;
    this.buttonHelper = new ButtonHelper(this.dependencies);
    this.copyConfirmationMessage = this.dependencies.translateFn('Copied');
  }

  public getSectionSpecificData(data: RawEventThreatInfo, permissions: ActionsPermissions): SpecificsThreatData {
    const sectionContent = [
      {
        label: this.renderer.getColumnNameFromKey(ColumnKeys.CATEGORY),
        content: this.renderer.getContentLabelFromValue(data.category, ColumnKeys.CATEGORY),
      },
    ];

    let specifics;
    switch (data.category) {
      case Category.FILE:
        specifics = this.getFileSpecifics(data, permissions);
        break;
      case Category.PROCESS:
        specifics = this.getProcessSpecifics(data, permissions);
        break;
      case Category.WEBSITE:
        specifics = this.getWebsiteSpecifics(data);
        break;
      case Category.EMAIL:
        specifics = this.getEmailSpecifics(data);
        break;
      case Category.APPLICATION:
        specifics = this.getApplicationSpecifics(data);
        break;
      case Category.CONNECTION:
        specifics = this.getConnectionSpecifics(data);
        break;
      case Category.DEVICE:
        specifics = this.getDeviceSpecifics(data);
        break;
      case Category.AMSI_OBJECTS:
        specifics = this.getAmsiSpecifics(data);
        break;
      case Category.UEFI_OBJECTS:
        specifics = this.getUefiSpecifics(data);
        break;
      default:
        throw new Error('Could not find any Category for threat');
    }

    return {
      title: specifics.title,
      sectionContent: sectionContent.concat(specifics.sectionContent),
      sectionActions: specifics.sectionActions,
    };
  }

  private getFileSpecifics(data: RawEventThreatInfo, permissions: ActionsPermissions): SpecificsThreatData {
    const sectionActions =
      permissions.blocklist === ActionPermissionState.MISSING_BLOCKLIST
        ? [this.getAddAsExclusionActionConfig(data, permissions.exclusion)]
        : [
            this.getAddAsExclusionActionConfig(data, permissions.exclusion),
            this.getAddToBlockListActionConfig(data, permissions.blocklist),
          ];

    const details = this.getDetectionDetails(data);

    return {
      title: this.dependencies.translateFn('File info').toUpperCase(),
      sectionContent: [
        {
          label: this.dependencies.translateFn('File path'),
          content: details,
          canCopy: !!details,
          clampLine: !!details,
          copyConfirmationMessage: this.copyConfirmationMessage,
        },
        {
          label: this.renderer.getColumnNameFromKey(ColumnKeys.COMMAND_LINE),
          content: data.commandLine || '-',
          canCopy: !!data.commandLine,
          clampLine: !!data.commandLine,
          copyConfirmationMessage: this.copyConfirmationMessage,
        },
        {
          label: this.renderer.getColumnNameFromKey(ColumnKeys.SHA_256),
          content: data.sha256 || '-',
          canCopy: !!data.sha256,
          copyConfirmationMessage: this.copyConfirmationMessage,
        },
      ],
      sectionActions: sectionActions,
    };
  }

  private getProcessSpecifics(data: RawEventThreatInfo, permissions: ActionsPermissions): SpecificsThreatData {
    const sectionContent = [
      {
        label: this.dependencies.translateFn('Process path'),
        content: data.details || '-',
        canCopy: !!data.details,
        copyConfirmationMessage: this.copyConfirmationMessage,
      },
      {
        label: this.dependencies.translateFn('Process ID'),
        content: data.processPid || '-',
      },
      {
        label: this.renderer.getColumnNameFromKey(ColumnKeys.COMMAND_LINE),
        content: data.commandLine || '-',
        canCopy: !!data.commandLine,
        copyConfirmationMessage: this.copyConfirmationMessage,
      },
      {
        label: this.dependencies.translateFn('Parent process path'),
        content: data.parentProcessPath || '-',
        canCopy: !!data.parentProcessPath,
        copyConfirmationMessage: this.copyConfirmationMessage,
      },
      {
        label: this.dependencies.translateFn('Parent process ID'),
        content: data.parentProcessPid || '-',
      },
      {
        label: this.renderer.getColumnNameFromKey(ColumnKeys.USER),
        content: data.user || '-',
      },
    ];
    const exploitWithTechnique = [ThreatType.EXPLOIT_EMERGING, ThreatType.EXPLOIT_OTHER, ThreatType.EXPLOIT_DPI];
    if (data.threatType && exploitWithTechnique.includes(data.threatType)) {
      sectionContent.unshift({
        label: this.dependencies.translateFn('Technique name'),
        content: data.techniqueName || '-',
      });
    }

    return {
      title: this.dependencies.translateFn('Process info').toUpperCase(),
      sectionContent,
      sectionActions: [this.getAddAsExclusionActionConfig(data, permissions.exclusion)],
    };
  }

  private getWebsiteSpecifics(data: RawEventThreatInfo) {
    return {
      title: this.dependencies.translateFn('Website info').toUpperCase(),
      sectionContent: [
        {
          label: this.dependencies.translateFn('URL'),
          content: data.details || '-',
          canCopy: !!data.details,
          copyConfirmationMessage: this.copyConfirmationMessage,
        },
      ],
    };
  }

  private getEmailSpecifics(data: RawEventThreatInfo) {
    return {
      title: this.dependencies.translateFn('Email info').toUpperCase(),
      sectionContent: [
        {
          label: this.dependencies.translateFn('Subject'),
          content: data.details || '-',
          canCopy: !!data.details,
          copyConfirmationMessage: this.copyConfirmationMessage,
        },
        {
          label: this.dependencies.translateFn('Sent by'),
          content: data.sender || '-',
          canCopy: !!data.sender,
          copyConfirmationMessage: this.copyConfirmationMessage,
        },
        {
          label: this.dependencies.translateFn('Sent to'),
          content: data.sentTo?.join(',') || '-',
          canCopy: !!data.sentTo?.join(','),
          copyConfirmationMessage: this.copyConfirmationMessage,
        },
      ],
    };
  }

  private getApplicationSpecifics(data: RawEventThreatInfo) {
    return {
      title: this.dependencies.translateFn('Application info').toUpperCase(),
      sectionContent: [
        {
          label: this.dependencies.translateFn('Application path'),
          content: data.details || '-',
          canCopy: !!data.details,
          copyConfirmationMessage: this.copyConfirmationMessage,
        },
        {
          label: this.renderer.getColumnNameFromKey(ColumnKeys.USER),
          content: data.user || '-',
        },
      ],
    };
  }

  private getConnectionSpecifics(data: RawEventThreatInfo) {
    return {
      title: this.dependencies.translateFn('Connection info').toUpperCase(),
      sectionContent: [
        {
          label: this.dependencies.translateFn('Detected item'),
          content: data.details || '-',
        },
        {
          label: this.renderer.getColumnNameFromKey(ColumnKeys.USER),
          content: data.user || '-',
        },
        {
          label: this.dependencies.translateFn('URL'),
          content: data.url || '-',
          canCopy: !!data.url,
          copyConfirmationMessage: this.copyConfirmationMessage,
        },
      ],
    };
  }

  private getDeviceSpecifics(data: RawEventThreatInfo): SpecificsThreatData {
    return {
      title: this.dependencies.translateFn('Device info').toUpperCase(),
      sectionContent: [
        {
          label: this.dependencies.translateFn('Device name'),
          content: data.details || '-',
        },
        {
          label: this.renderer.getColumnNameFromKey(ColumnKeys.USER),
          content: data.user || '-',
        },
      ],
    };
  }

  private getAmsiSpecifics(data: RawEventThreatInfo) {
    return {
      title: this.dependencies.translateFn('AMSI objects info').toUpperCase(),
      sectionContent: [
        {
          label: this.dependencies.translateFn('AMSI caller'),
          content: data.details?.split('=>')[0] || '-',
          canCopy: !!data.details?.split('=>')[0],
          copyConfirmationMessage: this.copyConfirmationMessage,
        },
        {
          label: this.dependencies.translateFn('Analyzed object'),
          content: data.details?.split('=>')[1] || '-',
          canCopy: !!data.details?.split('=>')[1],
          copyConfirmationMessage: this.copyConfirmationMessage,
        },
        {
          label: this.renderer.getColumnNameFromKey(ColumnKeys.COMMAND_LINE),
          content: data.commandLine || '-',
          canCopy: !!data.commandLine,
          copyConfirmationMessage: this.copyConfirmationMessage,
        },
        {
          label: this.dependencies.translateFn('Parent process path'),
          content: data.parentProcessPath || '-',
          canCopy: !!data.parentProcessPath,
          copyConfirmationMessage: this.copyConfirmationMessage,
        },
        {
          label: this.dependencies.translateFn('Parent process ID'),
          content: data.parentProcessPid || '-',
        },
        {
          label: this.renderer.getColumnNameFromKey(ColumnKeys.USER),
          content: data.user || '-',
        },
      ],
    };
  }

  private getUefiSpecifics(data: RawEventThreatInfo) {
    return {
      title: this.dependencies.translateFn('File info').toUpperCase(),
      sectionContent: [
        {
          label: this.dependencies.translateFn('File path'),
          content: data.details || '-',
        },
        {
          label: this.dependencies.translateFn('Command-line'),
          content: data.commandLine || '-',
        },
        {
          label: this.dependencies.translateFn('Sha256').toUpperCase(),
          content: data.sha256 || '-',
        },
      ],
    };
  }

  private getAddAsExclusionActionConfig(data: RawEventThreatInfo, state: ActionPermissionState): SectionAction {
    return {
      type: SectionActionType.GZ_BUTTON,
      label: this.dependencies.translateFn('Add exclusion'),
      eventName: DetailsPanelEvents.CREATE_EXCLUSIONS,
      eventData: {
        threatCategory: data.category,
        threatDetails: data.details,
        threatType: data.threatType,
      },
      itemConfig: {
        type: ButtonTypes.QUIET,
        color: ButtonColors.PRIMARY,
        disabled: !this.buttonHelper.isButtonEnabled(state),
        tooltip: this.buttonHelper.getTooltipString(state),
        id: 'addExclusion',
      },
    };
  }

  private getAddToBlockListActionConfig(data: RawEventThreatInfo, state: ActionPermissionState): SectionAction {
    const date = data.detectedOn ? getFormatedDate(data.detectedOn as any, this.dependencies.getTimezone()) : '';
    // the regex /^.*[\\\/]/ will replace the chars from the start of the string to the last char of type '/' or '\'
    const blockListNote = `${data.details?.replace(/^.*[\\\/]/, '') ?? ''}; ${this.dependencies.translateFn(
      'added from Threats Xplorer on'
    )} ${date}`;
    return {
      type: SectionActionType.GZ_BUTTON,
      label: this.dependencies.translateFn('Add to Blocklist'),
      eventName: DetailsPanelEvents.ADD_TO_BLOCK_LIST,
      eventData: {
        blockListNote: blockListNote,
        hash: data.sha256,
      },
      itemConfig: {
        type: ButtonTypes.QUIET,
        color: ButtonColors.PRIMARY,
        disabled: !this.buttonHelper.isButtonEnabled(state),
        tooltip: this.buttonHelper.getTooltipString(state),
        id: 'addToBlocklist',
      },
    };
  }

  private getDetectionDetails(data: RawEventThreatInfo) {
    let details;
    switch(data.threatType) {
      case ThreatType.VULNERABLE_DRIVERS:
        details = data.detectionPath;
        break;
      default:
        details = data.details;
    }

    return details || '-';
  }
}
